<script setup lang="ts">
import { computed, ref, PropType, watch } from "vue";
import { StepNavItem, ValidateableComponent } from "../../types/PersologOnlineTypes.ts";
import { isMobileView } from "../../utils/GeneralUtils.ts";
import ConfigureParticipantsTab from "../projects/configure-tabs/ConfigureParticipantsTab.vue";

const properties = defineProps({
    steps: {
        type: Array as PropType<StepNavItem[]>,
        default: () => [],
    },
    backLabel: {
        type: String,
        required: true,
    },
    nextLabel: {
        type: String,
        required: true,
    },
    finalLabel: {
        type: String,
        required: true,
    },
    // set by the parent while the step list itself is not final yet
    nextDisabled: {
        type: Boolean,
        default: false,
    },
});

// holds references for all steps
const stepRefs = ref<Array<unknown>>([]);

// navigation back and forth
const currentStep = ref(1); // PrimeVue Stepper starts from 1
const stepReached = ref(1);
const nextButtonDisabled = ref<boolean>(true);

const stepBack = () => {
    if (currentStep.value > 1) {
        currentStep.value--;
    }
};

const stepNext = () => {
    if (isCurrentStepValid()) {
        if (currentStep.value < properties.steps.length) {
            currentStep.value++;
            stepReached.value = Math.max(stepReached.value, currentStep.value); // Update furthest step reached
        }
    }
};

defineExpose({ currentStep, stepBack, stepNext });

const continueLabel = computed(() =>
    currentStep.value == properties.steps.length ? properties.finalLabel : properties.nextLabel,
);

/**
 * Check if the current step is valid
 * currentComponent is a ValidateableComponent defined in types/PersologOnlineTypes.ts
 */
const isCurrentStepValid = () => {
    const currentComponent = stepRefs.value[currentStep.value - 1] as ValidateableComponent;
    if (currentComponent && (currentComponent as InstanceType<typeof ConfigureParticipantsTab>).addParticipant) {
        (currentComponent as InstanceType<typeof ConfigureParticipantsTab>).addParticipant();
    }
    return currentComponent && currentComponent.validate?.();
};

const handleValidity = (index: number, valid: boolean) => {
    if (!valid) stepReached.value = Math.min(stepReached.value, currentStep.value);
    nextButtonDisabled.value = index == currentStep.value && !valid;
};

const isStepDisabled = (index: number) => index > stepReached.value;

// update disabled states
const stepsModel = ref(
    properties.steps.map((step: StepNavItem, index) => ({
        ...step,
        disabled: isStepDisabled(index + 1),
    })),
);

watch(stepReached, () => {
    stepsModel.value.forEach((step: StepNavItem, index: number) => {
        step.disabled = isStepDisabled(index + 1);
    });
});

watch(properties.steps, () => {
    stepsModel.value = properties.steps.map((step: StepNavItem, index) => ({
        ...step,
        disabled: isStepDisabled(index + 1),
    }));
});
</script>

<template>
    <Stepper v-model:value="currentStep">
        <StepList class="mx-2 md:mx-20 pt-8 md:pt-16 mb-8 md:mb-2 flex justify-center">
            <Step v-for="(step, index) in stepsModel" :key="index" as-child :value="index + 1">
                <div class="mx-2">
                    <div class="flex justify-center">
                        <div class="row">
                            <Button
                                rounded
                                :severity="step.disabled ? 'secondary' : 'primary'"
                                :disabled="step.disabled"
                                :icon="step.icon"
                                size="large"
                                @click="currentStep = index + 1"
                                :pt="{
                                    root: {
                                        style: {
                                            width: '3rem',
                                            height: '3rem',
                                            border: step.disabled
                                                ? '2px solid var(--p-divider-border-color)'
                                                : '2px solid var(--p-primary-color)',
                                        },
                                    },
                                }"
                            ></Button>
                        </div>
                    </div>
                </div>
                <Divider
                    class="mt-1"
                    :pt="{ root: { style: { 'border-top': '1.5px solid var(--p-divider-border-color)' } } }"
                    v-if="index + 1 < stepsModel.length"
                />
            </Step>
        </StepList>
        <StepList v-if="!isMobileView()" class="mx-0 md:mx-6 mb-12 flex justify-between">
            <Step v-for="(step, index) in stepsModel" :key="index" as-child :value="index + 1">
                <div style="min-width: 14rem">
                    <span class="text-center">{{ index + 1 }}. {{ step.label }}</span>
                </div>
            </Step>
        </StepList>

        <StepPanels>
            <StepPanel v-for="(stepComponent, index) in properties.steps" :key="index" :value="index + 1">
                <component
                    :is="stepComponent.component"
                    :ref="(el: any) => (stepRefs[index] = el)"
                    v-bind="stepComponent.props"
                    v-on="stepComponent.events"
                    v-if="currentStep == index + 1"
                    @valid="(validity: boolean) => handleValidity(index, validity)"
                />
            </StepPanel>
            <div class="flex justify-between mt-8">
                <Button
                    id="step-navigation_btn_back"
                    :label="backLabel"
                    @click="stepBack"
                    :class="{ invisible: currentStep === 1 }"
                />
                <Button
                    id="step-navigation_btn_continue"
                    :label="continueLabel"
                    @click="stepNext"
                    :disabled="nextButtonDisabled || nextDisabled"
                    :loading="nextButtonDisabled && currentStep == properties.steps.length"
                />
            </div>
        </StepPanels>
    </Stepper>
</template>

<style scoped>
.invisible {
    visibility: hidden;
}
</style>
